import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { CommandCenterPersonProfile, GlobalAccountPersonOrgRole } from '@ndvr/customer-api';

import { Sheet, SheetBody, SheetContent, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { formatPhoneNumber, getAddress, getPersonInitials, getPersonName, useHouseholdRoutePaths } from 'utils';
import { useGlobalAccount } from 'views/household/household-queries';
import {
    Button,
    DateTimeDisplay,
    HorizontalLayout,
    HouseholdLogoCard,
    PortfolioLogoCard,
    Text,
    Title,
    VerticalLayout,
} from 'widgets';
import { Avatar } from 'widgets/Avatar';
import { NotFoundTile } from 'widgets/drawer/NotFoundTile';

import { usePersonOrgFinancialProfile } from './person-queries';

interface PersonDetailDrawerProps {
    person: CommandCenterPersonProfile;
    onClose: () => void;
}

export const PersonDetailsDrawer = ({ person, onClose }: PersonDetailDrawerProps) => {
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const personOrgGUID = person.personOrgShortProfile.personOrgGUID;
    const { data: personOrgFinancialProfile, isLoading } = usePersonOrgFinancialProfile(personOrgGUID);

    const hasPersonOrgRoles =
        personOrgFinancialProfile?.profile?.globalAccountPersonOrgRoles &&
        0 < personOrgFinancialProfile?.profile?.globalAccountPersonOrgRoles.length;
    const hasPortfolioProfiles =
        personOrgFinancialProfile?.portfolioProfiles && 0 < personOrgFinancialProfile?.portfolioProfiles.length;
    const isAccountOwner =
        hasPortfolioProfiles &&
        personOrgFinancialProfile?.portfolioProfiles?.some((p) => p.portfolio.ownerPersonOrgGUID === personOrgGUID);

    return (
        <Sheet defaultOpen onExitComplete={onClose}>
            <SheetContent aria-describedby={undefined} layout="scroll-body" size="sm">
                <SheetTitle className="sr-only">{getPersonName(person.personOrgShortProfile)}</SheetTitle>
                <SheetBody>
                    <Section $columnCenter $gap={16}>
                        <Avatar size={96}>
                            <AvatarText>{getPersonInitials(person.personOrgShortProfile)}</AvatarText>
                        </Avatar>
                        <Title modal>{getPersonName(person.personOrgShortProfile)}</Title>
                        {isAccountOwner ? (
                            <HorizontalLayout>{isAccountOwner ? <Chip>Account Owner</Chip> : null}</HorizontalLayout>
                        ) : null}
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title modal>Detail</Title>
                        <Detail title="GUID" value={personOrgGUID} />
                        <Detail
                            title="Primary Email"
                            value={person.personOrgShortProfile?.primaryEmail?.plainText ?? '-'}
                        />
                        <Detail
                            title="Primary Phone Number"
                            value={formatPhoneNumber(person.personOrgShortProfile?.personalPhone?.plainText) ?? '-'}
                        />
                        <Detail title="Primary Address" value={getAddress(person.personOrgShortProfile) || '-'} />
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title modal>Activity</Title>
                        <Detail title="Last Login" value={<DateTimeDisplay dateString={person?.lastLoginAt} />} />
                    </Section>
                    <Separator />
                    <Section $gap={24}>
                        <Title modal>Households</Title>
                        {!isLoading && !hasPersonOrgRoles ? <NotFoundTile title="No households defined" /> : null}
                        {personOrgFinancialProfile?.profile?.globalAccountPersonOrgRoles.map(
                            (globalAccountPersonOrgRole) => (
                                <HouseholdItem
                                    key={globalAccountPersonOrgRole.globalAccountGUID}
                                    globalAccountPersonOrgRole={globalAccountPersonOrgRole}
                                />
                            ),
                        )}
                    </Section>
                    <Separator />
                    <Section $gap={16}>
                        <Title modal>Account Owner</Title>
                        {!isLoading && !hasPortfolioProfiles ? <NotFoundTile title="No accounts defined" /> : null}
                        {personOrgFinancialProfile?.portfolioProfiles.map((portfolioProfile) => (
                            <PortfolioLogoCard
                                key={portfolioProfile.portfolio.portfolioGUID}
                                name={portfolioProfile.portfolio.name.plainText}
                                portfolioTaxType={
                                    portfolioProfile.portfolio.investmentSetup.portfolioTaxProfile.portfolioTaxType
                                }
                                to={PortfolioPageRoutePath(
                                    portfolioProfile.portfolio.globalAccountGUID,
                                    portfolioProfile.portfolio.accountGroupGUID,
                                )}
                            />
                        ))}
                    </Section>
                </SheetBody>
                <SheetFooter variant="full-width">
                    <ButtonSection person={person} />
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

const AvatarText = styled(Text)`
    font-size: 24px;
`;

const Chip = styled(Text)`
    color: ${({ theme }) => theme.colors.cocoa900};
    background-color: ${({ theme }) => theme.colors.cocoa100};
    padding: 8px;
    border-radius: 4px;
`;

const Section = styled(VerticalLayout)`
    position: relative;
    padding: 1.5rem;
`;

const Detail = ({ title, value }: { title: string; value?: string | ReactNode }) => {
    return (
        <DetailLayout>
            <Text body>{title}</Text>
            <Text body>{value}</Text>
        </DetailLayout>
    );
};

const DetailLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    & > *:last-child {
        text-align: right;
    }
`;

const HouseholdItem = ({ globalAccountPersonOrgRole }: { globalAccountPersonOrgRole: GlobalAccountPersonOrgRole }) => {
    const { PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    const { data: household, isLoading, isError } = useGlobalAccount(globalAccountPersonOrgRole.globalAccountGUID);
    if (isLoading) return null;

    if (isError) {
        return <Text>Error: Could not load household: {globalAccountPersonOrgRole.globalAccountGUID}</Text>;
    }

    return (
        <HouseholdLogoCard
            name={household?.globalAccountNamePlain || ''}
            to={PortfolioOverviewTabRoutePath(globalAccountPersonOrgRole.globalAccountGUID)}
        />
    );
};

const ButtonSection = ({ person }: { person: CommandCenterPersonProfile }) => {
    const navigate = useNavigate();
    return (
        <ButtonHorizontalLayout>
            <ViewPersonButton
                buttonType="secondary"
                size="large"
                onClick={() => navigate(`/person/${person.personOrgShortProfile.personOrgGUID}`)}
            >
                View Person
            </ViewPersonButton>
        </ButtonHorizontalLayout>
    );
};

const ButtonHorizontalLayout = styled(HorizontalLayout)`
    border-top: 1px solid ${({ theme }) => theme.border.light};
    padding: 1.5rem;
`;

const ViewPersonButton = styled(Button)`
    width: 100%;
`;

const Separator = styled.div`
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.border.light};
`;
